import {
  getPluginContent as getPluginContentBase,
  renderTemplate as renderTemplateBase,
  checkAppClientUrl,
  getAppClientData,
  prepAppClientData,
  submitDataForFileSave as submitDataForFileSaveBase,
  getTemplateSuggestions as getTemplateSuggestionsBase,
  verifyFile as verifyFileBase,
  getDiscovery as getDiscoveryBase,
  inspectTemplate as inspectTemplateBase,
} from '@knapsack/app-client-api';
import type {
  GenericResponse,
  RendererId,
  TemplateInfo,
} from '@knapsack/types';
import { fetcher } from '@knapsack/utils';
import { queryOptions } from '@tanstack/react-query';
import urlJoin from 'url-join';
import type {
  RouteRendererDiscovery,
  SaveDataRoute,
} from '@knapsack/app/api-info';
import { getAccessTokenFromMem } from '@/domains/users/auth/client/access-token-storage';
import { knapsackGlobal } from '../global';
import { ksUrls } from './ks-urls';
import { queryClient } from './react-query';
import { log } from './logger';

export { checkAppClientUrl, getAppClientData, prepAppClientData };

export function getAppClientUrlBase(): string {
  const env = knapsackGlobal.appService?.getSnapshot().context.site?.env;
  if (!env) {
    throw new Error(`Cannot use App Client Environment before it's available.`);
  }
  if (env.type === 'none') {
    throw new Error(
      `Cannot use App Client Environment APIs because there is no environment.`,
    );
  }
  return env.url;
}

/** Verify a file exists */
export async function verifyFile(
  payload: Parameters<typeof verifyFileBase>[0]['payload'],
) {
  if (!payload.path) {
    throw new Error(`Path is required`);
  }
  const appClientUrl = getAppClientUrlBase();
  return queryClient.fetchQuery({
    queryKey: ['verifyFile', payload, appClientUrl],
    queryFn: () =>
      verifyFileBase({
        appClientUrl,
        payload,
      }),
  });
}

/**
 * Save data up on server to be used in template rendering with `dataId` query param later
 * @returns dataId that is md5 hash
 */
let localSaveDataEndpointNotFound = false;
export async function saveData({
  data,
  isLocal,
  envUrl,
}: {
  data: Record<string, unknown>;
  isLocal: boolean;
  envUrl: string | undefined;
}): Promise<string> {
  if (isLocal && envUrl) {
    // post to local server
    // failaack to app api if not exists
    if (!localSaveDataEndpointNotFound) {
      try {
        const localUrl = urlJoin(
          envUrl,
          '/api/v1/save-data' satisfies SaveDataRoute['path'],
        );

        const body: SaveDataRoute['input'] = data;
        const result = await fetcher<SaveDataRoute['output']>({
          url: localUrl,
          body,
        });
        const {
          ok,
          data: { dataId },
        } = result;
        if (!ok || !dataId) {
          log.error(
            `Local server sent bad save-data response, will not try again`,
            {
              metadata: {
                result,
              },
            },
          );
          localSaveDataEndpointNotFound = true;
          // fallback to app api and do not try again
        } else {
          return dataId;
        }
      } catch (error) {
        localSaveDataEndpointNotFound = true;
        // don't want to send to datadog because it's expected to happen
        console.log(
          'Local server missing save-data endpoint, will not try again',
          {
            error,
          },
        );
      }
    }
  }
  const {
    ok,
    data: { hash },
  } = await fetcher<GenericResponse<{ hash: string }>>({
    url: urlJoin(ksUrls.apiRootEndpoint, '/render-data'),
    body: data,
  });
  if (!ok || !hash) {
    const error = new Error(
      `Error in saveData() when trying to store custom form edit data.`,
    );
    log.error(error.message, { error });
    throw error;
  }
  return hash;
}

export async function getPluginContent({ pluginId }: { pluginId: string }) {
  return getPluginContentBase({
    pluginId,
    appClientUrl: getAppClientUrlBase(),
  });
}

export async function renderTemplate(
  options: Parameters<typeof renderTemplateBase>[0]['options'],
) {
  return renderTemplateBase({
    options,
    appClientUrl: getAppClientUrlBase(),
  });
}

export async function submitDataForFileSave(
  state: Parameters<typeof submitDataForFileSaveBase>[0]['state'],
) {
  return submitDataForFileSaveBase({
    urlBase: getAppClientUrlBase(),
    state,
  });
}

export async function getTemplateSuggestions(
  query: Parameters<typeof getTemplateSuggestionsBase>[0]['query'],
) {
  return getTemplateSuggestionsBase({
    appClientUrl: getAppClientUrlBase(),
    query,
  });
}

export async function getDiscovery({
  appClientUrl = getAppClientUrlBase(),
}: {
  appClientUrl?: string;
} = {}) {
  const accessToken = getAccessTokenFromMem();

  return getDiscoveryBase({
    appClientUrl,
    authHeader: accessToken ? `Bearer ${accessToken}` : undefined,
  });
}

export async function getRendererDiscovery<TheRendererId extends RendererId>({
  appClientUrl = getAppClientUrlBase(),
  rendererId,
}: {
  appClientUrl?: string;
  rendererId: TheRendererId;
}) {
  const accessToken = getAccessTokenFromMem();
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.authorization = `Bearer ${accessToken}`;
  }
  return fetcher<RouteRendererDiscovery<TheRendererId>['output']>({
    url: urlJoin(
      appClientUrl,
      '/api/v2/renderer-discovery' satisfies RouteRendererDiscovery<TheRendererId>['path'],
    ),
    query: {
      rendererId,
    } satisfies RouteRendererDiscovery<TheRendererId>['input'],
    headers,
  });
}

export async function inspectTemplateUncached({
  templateInfo,
  appClientUrl = getAppClientUrlBase(),
}: {
  templateInfo: Parameters<typeof inspectTemplateBase>[0]['templateInfo'];
  appClientUrl?: string;
}) {
  const accessToken = getAccessTokenFromMem();
  return inspectTemplateBase({
    appClientUrl,
    templateInfo,
    authHeader: accessToken ? `Bearer ${accessToken}` : undefined,
  });
}

export const createInspectTemplateOptions = ({
  templateInfo,
  appClientUrl,
}: {
  templateInfo: TemplateInfo;
  appClientUrl?: string;
}) =>
  queryOptions({
    queryKey: ['inspectTemplate', templateInfo, appClientUrl],
    enabled: !!templateInfo?.rendererId,
    queryFn: () => {
      return inspectTemplateUncached({
        templateInfo,
        appClientUrl,
      });
    },
  });

export const inspectTemplate = (
  opt: Parameters<typeof createInspectTemplateOptions>[0],
) => queryClient.fetchQuery(createInspectTemplateOptions(opt));
